/**
 * chrono — le temps passé sur une activité, et son relevé au serveur.
 *
 * L'ancien chrono (`TimerContainer` + `TimerActivityStep`) ajoutait une seconde par tic d'un
 * `setInterval` : le temps passé en arrière-plan était perdu en natif (les minuteurs JS s'y arrêtent),
 * et la reprise prévue pour lui (`handleAppStateChange`, qui ajoute le temps écoulé au retour) ne
 * tournait jamais (`statusRef` n'était pas passé). Ici le chrono est une HORLOGE : le temps acquis
 * (`baseMs`) et l'instant où il s'est remis à courir (`runningSince`) ; le temps passé se lit à toute
 * heure, arrière-plan compris — ce que l'ancien code voulait faire. La pause du tuteur l'arrête et le
 * reprend ; « J'ai fini » le fige.
 *
 * Une activité n'a qu'un chrono, même si deux écrans la montrent (l'ancien en montait deux : la séance
 * et la surcouche de Sam, chacun avec son `updateChrono` toutes les 30 s) : le magasin du module en
 * tient un par activité, compté par ses lecteurs, et un seul relevé toutes les 30 s. Le décompte à
 * l'écran vit dans `<ActivityChrono>` (qui reçoit `runningSince`) : rien d'autre ne se redessine
 * chaque seconde.
 */

/** Le relevé au serveur : toutes les 30 s, comme aujourd'hui. */
export const CHRONO_REPORT_INTERVAL_MS = 30_000

export type ChronoState = {
    /** Le temps acquis, en ms, jusqu'à `runningSince`. */
    baseMs: number
    /** `Date.now()` de la dernière reprise ; `null` à l'arrêt. */
    runningSince: number | null
    /** Le tuteur a mis l'activité en pause. */
    paused: boolean
    /** « J'ai fini » : le chrono est figé pour de bon. */
    stopped: boolean
}

export type ChronoEvent =
    | { type: "PAUSE"; now: number }
    | { type: "RESUME"; now: number }
    | { type: "STOP"; now: number }
    /** Le temps que le serveur a reçu (`activity.duration`, relu) : le chrono ne reste pas derrière lui. */
    | { type: "SERVER_SEEN"; seconds: number; now: number }

/** Un chrono qui part de ce que le serveur a gardé (`activity.duration`, en secondes). */
export function startedChrono(initialSeconds: number, now: number, paused = false): ChronoState {
    return { baseMs: Math.max(0, initialSeconds) * 1000, runningSince: paused ? null : now, paused, stopped: false }
}

/** Le temps passé, en ms. */
export function chronoElapsedMs(state: ChronoState, now: number): number {
    return state.baseMs + (state.runningSince != null ? Math.max(0, now - state.runningSince) : 0)
}

/** Le temps passé, en secondes entières : ce que `updateChrono` et `finalize` reçoivent. */
export function chronoSeconds(state: ChronoState, now: number): number {
    return Math.floor(chronoElapsedMs(state, now) / 1000)
}

export function chronoReducer(state: ChronoState, event: ChronoEvent): ChronoState {
    if (state.stopped) return state
    switch (event.type) {
        case "PAUSE":
            if (state.paused) return state
            return { ...state, baseMs: chronoElapsedMs(state, event.now), runningSince: null, paused: true }
        case "RESUME":
            if (!state.paused) return state
            return { ...state, runningSince: event.now, paused: false }
        case "STOP":
            return { ...state, baseMs: chronoElapsedMs(state, event.now), runningSince: null, stopped: true }
        case "SERVER_SEEN": {
            // Un autre onglet, un rechargement, l'app relancée : le chrono est reparti d'un compte gardé
            // en mémoire, plus ancien que ce que `update-chrono` a écrit. Le temps passé ne recule
            // jamais : le plus grand des deux gagne.
            const serverMs = Math.max(0, event.seconds) * 1000
            if (serverMs <= chronoElapsedMs(state, event.now)) return state
            return { ...state, baseMs: serverMs, runningSince: state.runningSince != null ? event.now : null }
        }
        default:
            return state
    }
}

/**
 * Ce que la page montre (`UIActivityChrono`) : le temps qui reste À L'INSTANT `now`, et, quand il
 * court, l'instant de ce relevé — `<ActivityChrono>` en tire lui-même le décompte.
 */
export type ChronoDisplay = {
    remainingSeconds: number
    estimatedSeconds: number
    paused: boolean
    runningSince: number | null
}

export function chronoDisplay(state: ChronoState, estimatedSeconds: number, now: number): ChronoDisplay {
    return {
        remainingSeconds: estimatedSeconds - chronoSeconds(state, now),
        estimatedSeconds,
        paused: state.paused,
        runningSince: state.runningSince != null ? now - (chronoElapsedMs(state, now) % 1000) : null,
    }
}

/**
 * Les deux seuils où l'ancien `TimerContainer` alertait l'élève, son et bandeau : bientôt la durée
 * prévue (95 %, le seuil où `<ActivityChrono>` passe à sa tinte d'alerte), puis la durée dépassée (101 %).
 */
const CHRONO_ALERTS = [
    { alert: "nearly_due", percent: 95 },
    { alert: "overdue", percent: 101 },
] as const

export type ChronoAlert = (typeof CHRONO_ALERTS)[number]["alert"]

/**
 * Dans combien de ms le chrono qui court franchit chacun des seuils encore devant lui. L'ancien sonnait à
 * la seconde où le temps passé valait `round(durée prévue × seuil / 100)` : un seuil déjà passé (une
 * activité rouverte au-delà, ou franchi avant une pause) ne sonne plus. À l'arrêt, en pause ou sans
 * durée prévue, rien ne sonne.
 */
export function chronoAlertsAhead(display: ChronoDisplay, now: number): { alert: ChronoAlert; inMs: number }[] {
    const { runningSince, estimatedSeconds, remainingSeconds, paused } = display
    if (runningSince == null || paused || estimatedSeconds <= 0) return []
    const elapsedMs = (estimatedSeconds - remainingSeconds) * 1000 + Math.max(0, now - runningSince)
    return CHRONO_ALERTS.map(({ alert, percent }) => ({
        alert,
        inMs: Math.round((estimatedSeconds * percent) / 100) * 1000 - elapsedMs,
    })).filter(({ inMs }) => inMs > 0)
}

/* ─────────────────────── le magasin : un chrono par activité ─────────────────────── */

type Entry = {
    state: ChronoState
    holders: number
    timer: ReturnType<typeof setInterval> | null
    listeners: Set<() => void>
    report: ((seconds: number) => void) | null
    /** Le dernier temps que le serveur a (relevé envoyé, ou relu) : le même ne repart pas. */
    reported: number
}

const entries = new Map<string, Entry>()

/** Pour les tests : vide le magasin. */
export function resetChronoStore(): void {
    entries.forEach((entry) => entry.timer != null && clearInterval(entry.timer))
    entries.clear()
}

export type ChronoHandle = {
    getState: () => ChronoState
    subscribe: (listener: () => void) => () => void
    dispatch: (event: ChronoEvent) => void
    /** Le relevé au serveur que ce lecteur fait (le dernier posé sert). */
    setReporter: (report: (seconds: number) => void) => void
    release: () => void
}

/**
 * Prend le chrono d'une activité. Le premier lecteur le crée (depuis `initialSeconds`) et lance le
 * relevé de 30 s ; les suivants lisent le même ; le dernier qui le rend arrête le relevé. Le chrono
 * lui-même reste en mémoire le temps de la session, comme l'ancien (`state.activity.timers`) : rouvrir
 * l'activité reprend là où l'élève en était. Il ne court que tenu : un lecteur rendu ne le pilote plus.
 */
export function acquireChrono(
    activityId: string | number,
    initialSeconds: number,
    now: () => number = Date.now
): ChronoHandle {
    const key = String(activityId)
    let entry = entries.get(key)
    if (entry == null) {
        entry = {
            state: startedChrono(initialSeconds, now()),
            holders: 0,
            timer: null,
            listeners: new Set(),
            report: null,
            reported: Math.max(0, initialSeconds),
        }
        entries.set(key, entry)
    } else if (!entry.state.stopped && !entry.state.paused && entry.state.runningSince == null) {
        entry.state = { ...entry.state, runningSince: now() }
    }
    const held = entry
    held.holders += 1
    if (held.timer == null) {
        held.timer = setInterval(() => {
            if (held.state.stopped || held.report == null) return
            // En pause (le tuteur, la fin du live), le temps ne bouge plus : le renvoyer toutes les
            // 30 s ne disait rien de nouveau au serveur.
            const seconds = chronoSeconds(held.state, now())
            if (seconds === held.reported) return
            held.reported = seconds
            held.report(seconds)
        }, CHRONO_REPORT_INTERVAL_MS)
    }
    let released = false
    return {
        getState: () => held.state,
        subscribe: (listener) => {
            held.listeners.add(listener)
            return () => held.listeners.delete(listener)
        },
        dispatch: (event) => {
            // Un lecteur rendu ne pilote plus rien : son activité est quittée, et une reprise ou un temps
            // venus après coup relanceraient une horloge que plus personne ne regarde.
            if (released) return
            if (event.type === "SERVER_SEEN") held.reported = Math.max(held.reported, Math.floor(event.seconds))
            const next = chronoReducer(held.state, event)
            if (next === held.state) return
            held.state = next
            held.listeners.forEach((listener) => listener())
        },
        setReporter: (report) => {
            if (released) return
            held.report = report
        },
        release: () => {
            if (released) return
            released = true
            held.holders -= 1
            if (held.holders > 0) return
            if (held.timer != null) clearInterval(held.timer)
            held.timer = null
            held.report = null
            // Plus personne ne regarde : le temps acquis est gardé, l'horloge s'arrête (l'ancien chrono
            // ne tournait que monté). Une activité rendue n'a plus de chrono à garder.
            if (held.state.stopped) entries.delete(key)
            else held.state = { ...held.state, baseMs: chronoElapsedMs(held.state, now()), runningSince: null }
        },
    }
}
